<template>
  <div
    :class="containerClasses"
    :style="isMobile ? mobileContainerStyle : undefined"
    @click="onContainerClick"
  >
    <svg-icon
      name="ic-search"
      class="text-color-4 flex-shrink-0"
      style="width: 24px; height: 24px"
    />
    <input
      ref="inputRef"
      v-model="inputValue"
      class="min-w-0 bg-transparent outline-none text-color-1 placeholder:text-color-4 search-input"
      :class="inputClasses"
      :style="inputStyle"
      :placeholder="$t(placeholderDto)"
      type="search"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
    />
    <button
      v-if="inputValue && showCloseIcon"
      type="button"
      class="text-color-4 hover:text-color-1 transition-colors flex-shrink-0"
      aria-label="Clear"
      @click.stop="clear"
    >
      <svg-icon name="ic-erase-input-close" style="width: 20px; height: 20px" />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

import { useDebounce, useMediaQuery } from '@vueuse/core'

import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholderDto: {
    type: String,
    default: '',
  },
  wrapperClasses: {
    type: String,
    default: '',
  },
  debounceMs: {
    type: Number,
    default: 500,
  },
  border: {
    type: String,
    default: 'border border-transparent',
  },
  padding: {
    type: String,
    default: 'p-4',
  },
  background: {
    type: String,
    default: 'bg-bg-3',
  },
  showCloseIcon: {
    type: Boolean,
    default: true,
  },
})

const emits = defineEmits(['onSearch', 'onBlur', 'update:modelValue'])

const isMobile = useMediaQuery('(max-width: 859px)')

const inputRef = ref(null)
const inputValue = ref('')
const focus = ref(false)
const isExpanded = ref(false)
const debouncedSearch = useDebounce(inputValue, () => props.debounceMs)

const containerClasses = computed(() => {
  if (!isMobile.value) {
    return [
      'flex',
      'items-center',
      'w-full',
      'rounded-[10px]',
      'text-color-4',
      'hover:border-brand',
      'focus-within:border-brand',
      'transition-colors',
      props.wrapperClasses,
      props.border,
      props.padding,
      props.background,
    ]
  }

  return [
    'flex',
    'items-center',
    props.wrapperClasses,
    'cursor-pointer',
    'rounded-[12px]',
    'border',
    'transition-[width,padding,border-color] duration-300 ease-out',
    isExpanded.value
      ? focus.value
        ? 'border-brand'
        : 'border-[#232323]'
      : 'border-[#232323] justify-center',
  ]
})

const mobileContainerStyle = computed(() => {
  if (isExpanded.value) {
    return {
      width: '349px',
      height: '56px',
      padding: '0 16px',
      flexShrink: '0',
    }
  }
  return { width: '56px', height: '56px', padding: '16px', flexShrink: '0' }
})

const inputClasses = computed(() => {
  if (!isMobile.value) return ['flex-1', 'ml-3', 'mr-3']

  if (isExpanded.value) {
    return [
      'flex-1',
      'ml-3',
      'mr-3',
      'opacity-100',
      'transition-opacity duration-200',
    ]
  }
  return [
    'flex-none',
    'w-0',
    'ml-0',
    'mr-0',
    'opacity-0',
    'pointer-events-none',
    'transition-opacity duration-200',
  ]
})

const inputStyle = computed(() => {
  if (!isMobile.value) return {}
  return { transitionDelay: isExpanded.value ? '100ms' : '0ms' }
})

function onContainerClick() {
  if (isMobile.value && !isExpanded.value) {
    isExpanded.value = true
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
}

function onBlur() {
  emits('onBlur', inputValue.value)
  focus.value = false
  if (isMobile.value && !inputValue.value) {
    isExpanded.value = false
  }
}

function onInput() {
  emits('update:modelValue', inputValue.value)
}

function onFocus() {
  focus.value = true
}

watch(debouncedSearch, (val) => {
  emits('onSearch', val)
})

watch(
  () => props.modelValue,
  (val) => {
    inputValue.value = val
    if (isMobile.value && !val && !focus.value) {
      isExpanded.value = false
    }
  },
  { immediate: true }
)

function clear() {
  inputValue.value = ''
  emits('update:modelValue', '')
  emits('onSearch', '')
  nextTick(() => {
    inputRef.value?.focus()
  })
}
</script>

<style scoped>
/* Hide native WebKit clear (x) button to avoid duplication with our custom clear button */
.search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
  display: none;
}
</style>
